import React from 'react';
import './ErrorBoundary.css';

/**
 * React 에러 경계 컴포넌트
 * - 하위 컴포넌트 에러 캐치
 * - 사용자 친화적인 에러 메시지
 * - 에러 리포팅 기능
 * - 페이지 새로고침 옵션
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // 에러가 발생하면 hasError를 true로 설정
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // 에러 정보 저장
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // 에러 로깅
    this.logError(error, errorInfo, errorId);
  }

  logError = (error, errorInfo, errorId) => {
    const errorData = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // 개발 환경에서는 콘솔에 출력
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error Boundary Caught Error [${errorId}]`);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Full Error Data:', errorData);
      console.groupEnd();
    }

    // 프로덕션 환경에서는 에러 리포팅 서비스로 전송
    if (process.env.NODE_ENV === 'production' && this.props.onError) {
      this.props.onError(errorData);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleCopyError = async () => {
    const { error, errorInfo, errorId } = this.state;
    
    // 복사할 에러 정보 구성
    const errorText = `
=== Error Report ===
Error ID: ${errorId || 'N/A'}
Time: ${new Date().toISOString()}
URL: ${window.location.href}

Error Message:
${error?.message || 'No error message'}

Stack Trace:
${error?.stack || 'No stack trace'}

Component Stack:
${errorInfo?.componentStack || 'No component stack'}
`.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      
      // 복사 성공 피드백 (버튼 텍스트 임시 변경)
      const button = document.querySelector('.error-boundary__copy-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ 복사됨!';
        button.classList.add('copied');
        
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('copied');
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy error:', err);
      alert('에러 정보를 복사할 수 없습니다.');
    }
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 에러 UI가 제공된 경우
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo);
      }

      // 기본 에러 UI
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__icon">
              <span>💥</span>
            </div>
            
            <h1 className="error-boundary__title">
              앗! 문제가 발생했습니다
            </h1>
            
            <p className="error-boundary__message">
              {this.props.message || 
               '예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 잠시 후 다시 시도해주세요.'}
            </p>

            {/* 에러 ID 표시 (고객 지원용) */}
            {this.state.errorId && (
              <div className="error-boundary__error-id">
                <small>오류 ID: {this.state.errorId}</small>
                <button 
                  onClick={this.handleCopyError}
                  className="error-boundary__copy-button"
                  title="에러 정보 복사"
                >
                  📋 에러 복사
                </button>
              </div>
            )}

            {/* 액션 버튼들 */}
            <div className="error-boundary__actions">
              <button 
                onClick={this.handleRetry}
                className="error-boundary__button error-boundary__button--primary"
              >
                다시 시도
              </button>
              
              <button 
                onClick={this.handleReload}
                className="error-boundary__button error-boundary__button--secondary"
              >
                페이지 새로고침
              </button>
              
              <button 
                onClick={this.handleGoHome}
                className="error-boundary__button error-boundary__button--outline"
              >
                홈으로 이동
              </button>
            </div>

            {/* 개발 환경에서만 에러 세부정보 표시 */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary__details">
                <summary>
                  개발자 정보 (상세 에러)
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleCopyError();
                    }}
                    className="error-boundary__copy-button error-boundary__copy-button--inline"
                    title="에러 정보 복사"
                  >
                    📋 복사
                  </button>
                </summary>
                <div className="error-boundary__error-details">
                  <h3>Error Message:</h3>
                  <pre>{this.state.error.message}</pre>
                  
                  <h3>Stack Trace:</h3>
                  <pre>{this.state.error.stack}</pre>
                  
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <h3>Component Stack:</h3>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    // 에러가 없으면 children 렌더링
    return this.props.children;
  }
}

export default ErrorBoundary;